import { QueryKey as TQueryKey, useQuery } from '@tanstack/react-query';
import type { UseMutationOptions } from '@tanstack/react-query/src/types';
import {
    getBudgetScenarios,
    getBudgetScenario,
    createBudgetScenario,
    updateBudgetScenario,
    deleteBudgetScenario,
    BudgetScenario,
} from '../../services/budgetScenario';
import { QueryKey } from '../../enums/QueryKey';
import { queryClient } from '../../services/queryClient';
import i18n from '../../i18n';
import { useCreateRecord, useUpdateRecord, useDeleteRecord } from './crudOperations';

// i18n-ns: budget (scenarioErrorMessage resolves its key via the global i18n instance)

/** True when the server rejected the write because another session changed the data (HTTP 409). */
export function isConflictError(error: Error): boolean {
    return (error as { response?: { status?: number } }).response?.status === 409;
}

/** The backend's 409 message is English prose — swap it for the translated conflict hint. */
export function scenarioErrorMessage(error: Error): string {
    return isConflictError(error) ? i18n.t('error.conflictRetry', { ns: 'budget' }) : error.message;
}

/** Loading/error surface shared by the scenario read hooks (sibling budget hook convention). */
export interface ScenarioQueryState {
    isError: boolean;
    error: Error | null;
    refetch: () => void;
}

/** Route params arrive as strings — reject NaN/zero/negative ids before they hit the API. */
export function isValidScenarioId(id: number | undefined): id is number {
    return id != null && Number.isInteger(id) && id > 0;
}

/** The React Query read for one scenario-scoped GET. Every such hook is the same query: keyed on the
 *  scenario (and the version below), gated on a usable id, returning the `[data, isLoading, state]`
 *  tuple the scenario pages destructure. Seven of them were byte-identical copies of this body.
 *
 *  `versionId` is part of the query KEY, not just the request (CALL-794): the option lists in these
 *  responses describe ONE budget version, so re-pointing the detail form's Budget select has to ask
 *  again instead of re-serving the previous version's pickers. Optional — omitted, the server answers
 *  from the scenario's stored version, exactly as before, which is what the reads that have no version
 *  of their own (the scenario row, the saved impact) rely on.
 *
 *  useScenarioConsolidated is deliberately NOT built from this: it keys on a VERSION plus a selection
 *  rather than a scenario, gates on that selection being resolved, and keeps the previous board on
 *  screen (keepPreviousData, hence isFetching rather than isLoading). Each of those would have to
 *  become an option here, which is more plumbing than the one copied body it would remove. */
export const createScenarioQuery = <TResponse>(
    queryKeyRoot: (typeof QueryKey)[keyof typeof QueryKey],
    fetchFn: (scenarioId: number, versionId?: number) => Promise<TResponse>
) => {
    const useScenarioQuery = (
        scenarioId: number | undefined,
        versionId?: number
    ): [TResponse | undefined, boolean, ScenarioQueryState] => {
        const enabled = isValidScenarioId(scenarioId);
        const { data, isLoading, isError, error, refetch } = useQuery<TResponse, Error>({
            queryKey: [queryKeyRoot, scenarioId, versionId] as TQueryKey,
            queryFn: () => fetchFn(scenarioId!, versionId),
            enabled,
        });

        return [
            data,
            enabled && isLoading,
            {
                isError,
                error: error ?? null,
                refetch: () => {
                    if (enabled) refetch();
                },
            },
        ];
    };

    return useScenarioQuery;
};

function useScenarioList(
    bucketId: number | undefined,
    enabled: boolean
): [BudgetScenario[], boolean, ScenarioQueryState] {
    const { data, isLoading, isFetching, isFetchedAfterMount, isError, error, refetch } = useQuery<
        BudgetScenario[],
        Error
    >({
        // The unscoped list keeps its BARE key: useCreateRecord appends the new scenario with an
        // exact-key setQueryData([QueryKey.BudgetScenarios]), so a trailing `undefined` here would put
        // the optimistic row in a cache entry nothing reads. Prefix invalidation covers both shapes.
        // exhaustive-deps only reads array literals, so the spread hides a `bucketId` that is in fact
        // always keyed whenever it is sent.
        // eslint-disable-next-line @tanstack/query/exhaustive-deps
        queryKey: [QueryKey.BudgetScenarios, ...(bucketId == null ? [] : [bucketId])] as TQueryKey,
        queryFn: () => getBudgetScenarios(bucketId),
        initialData: [],
        enabled,
    });

    return [data, isFetchedAfterMount ? isLoading : isFetching, { isError, error: error ?? null, refetch }];
}

/** Every scenario of every bucket — the overview's list. Its grid column filters do the scoping, so a
 *  server-side filter here would take the "all budgets at a glance" view away. */
export const useBudgetScenarios = () => useScenarioList(undefined, true);

/** CALL-794: one bucket's scenarios, for the consolidated page — it can only overlay bucket-mates, so
 *  the rest of the table was downloaded and thrown away. Gated on the bucket rather than defaulting to
 *  "all" while it resolves: the bucket comes from the selected VERSION, which is undefined for the first
 *  renders, and an ungated call would fetch the whole table once before asking again for the bucket. */
export const useBucketScenarios = (bucketId: number | undefined) => useScenarioList(bucketId, bucketId != null);

export const useBudgetScenario = createScenarioQuery(QueryKey.BudgetScenario, getBudgetScenario);

// A scenario write now carries the type-specific inputs too (CALL-783), so it touches everything
// derived from them: the scenario itself (name, version, period), the input lists, the calculated
// impact, and — when the scenario is part of a consolidated view — the cross-scenario aggregation.
// The input keys are invalidated without an id: only the mounted scenario's query is active, so the
// id-less prefix costs nothing and cannot miss the one that matters.
const invalidateScenarioDependents = () => {
    queryClient.invalidateQueries({ queryKey: [QueryKey.BudgetScenario] });
    queryClient.invalidateQueries({ queryKey: [QueryKey.BudgetScenarioContactValues] });
    queryClient.invalidateQueries({ queryKey: [QueryKey.BudgetScenarioMinutePriceValues] });
    queryClient.invalidateQueries({ queryKey: [QueryKey.BudgetScenarioShiftRows] });
    queryClient.invalidateQueries({ queryKey: [QueryKey.BudgetScenarioBudgetAdjustment] });
    queryClient.invalidateQueries({ queryKey: [QueryKey.BudgetScenarioDriverRows] });
    queryClient.invalidateQueries({ queryKey: [QueryKey.BudgetScenarioImpact] });
    // The draft previews are cached per payload, and a save moves the baseline they were computed on —
    // so drop them rather than let the next round of edits be answered from a pre-save cache entry.
    queryClient.invalidateQueries({ queryKey: [QueryKey.BudgetScenarioImpactPreview] });
    queryClient.invalidateQueries({ queryKey: [QueryKey.BudgetScenarioConsolidated] });
};

export function useCreateBudgetScenario({
    onSettled,
}: Pick<UseMutationOptions<BudgetScenario, Error, Parameters<typeof createBudgetScenario>[0]>, 'onSettled'> = {}) {
    return useCreateRecord<BudgetScenario, Error, Parameters<typeof createBudgetScenario>[0]>({
        createRecordFn: createBudgetScenario,
        fetchRecordsQueryKey: [QueryKey.BudgetScenarios],
        recordName: 'Scenario',
        onSettled,
    });
}

export function useUpdateBudgetScenario({
    onSettled,
}: Pick<UseMutationOptions<BudgetScenario, Error, Parameters<typeof updateBudgetScenario>[0]>, 'onSettled'> = {}) {
    return useUpdateRecord<BudgetScenario, Error, Parameters<typeof updateBudgetScenario>[0]>({
        updateRecordFn: updateBudgetScenario,
        fetchRecordsQueryKey: [QueryKey.BudgetScenarios],
        recordName: 'Scenario',
        // The whole-form save can lose an optimistic-concurrency race; the toast must say so in the
        // user's language rather than repeat the backend's English sentence.
        errorMessage: scenarioErrorMessage,
        onSettled: (data, error, variables, context) => {
            invalidateScenarioDependents();
            return onSettled?.(data, error, variables, context);
        },
    });
}

export function useDeleteBudgetScenario({
    onSettled,
}: Pick<UseMutationOptions<unknown, Error, number>, 'onSettled'> = {}) {
    return useDeleteRecord<unknown, Error, number>({
        deleteRecordFn: deleteBudgetScenario,
        fetchRecordsQueryKey: [QueryKey.BudgetScenarios],
        recordName: 'Scenario',
        onSettled: (data, error, variables, context) => {
            invalidateScenarioDependents();
            return onSettled?.(data, error, variables, context);
        },
    });
}
